import { type ReactNode } from "react";

import {
  Switch as ReactAriaSwitch,
  type SwitchProps as ReactAriaSwitchProps,
} from "react-aria-components";

import { cn } from "#utils";

export interface ModeToggleButtonProps extends Omit<ReactAriaSwitchProps, "children"> {
  /** Icon to display for the left option */
  leftIcon: ReactNode;
  /** Label to display for the left option */
  leftLabel: string;
  /** Icon to display for the right option */
  rightIcon: ReactNode;
  /** Label to display for the right option */
  rightLabel: string;
  /** Additional class name for the container */
  className?: string;
}

/**
 * ModeToggleButton component for switching between two distinct modes, each with icons and labels.
 *
 * @example
 * Basic usage:
 * ```tsx
 * <ModeToggleButton
 *   isSelected={isSelected}
 *   leftIcon={<AirplayIcon size={14} />}
 *   leftLabel="Visual Editor"
 *   onChange={setIsSelected}
 *   rightIcon={<CodeBlockIcon size={14} />}
 *   rightLabel="JSON Editor"
 * />
 * ```
 *
 * @remarks
 * To prevent the ModeToggleButton component from changing width (jumping) when switching
 * between modes with different label/icon lengths, set a fixed width on the container
 * or parent element of the ModeToggleButton.
 *
 * @example
 * Prevent jumping by setting the container width:
 * ```tsx
 * <div className="w-[160px]">
 *   <ModeToggleButton ... />
 * </div>
 * ```
 */
export function ModeToggleButton({
  isSelected,
  leftIcon,
  leftLabel,
  rightIcon,
  rightLabel,
  className,
  ...props
}: ModeToggleButtonProps) {
  return (
    <ReactAriaSwitch
      className={cn(
        "flex items-center justify-between rounded-xl bg-background-primary p-xxs shadow-[inset_0_0_0.5rem_0.125rem_rgba(0,0,0,0.25)]",
        "hover:outline-2 hover:outline-interactive-primary-button-hover-background hover:[outline-style:solid]",
        "data-[focus-visible]:outline-2 data-[focus-visible]:outline-interactive-primary-button-focus-background data-[focus-visible]:[outline-style:solid]",
        "data-[focused]:outline-2 data-[focused]:outline-interactive-primary-button-focus-background data-[focused]:[outline-style:solid]",
        className,
      )}
      isSelected={isSelected}
      {...props}
    >
      {({ isSelected: selected }) => (
        <>
          <ModeToggleButtonOption isSelected={!selected}>
            {!selected ? leftLabel : leftIcon}
          </ModeToggleButtonOption>

          <ModeToggleButtonOption isSelected={selected}>
            {selected ? rightLabel : rightIcon}
          </ModeToggleButtonOption>
        </>
      )}
    </ReactAriaSwitch>
  );
}

interface ModeToggleButtonOptionProps {
  /** Whether the option is selected */
  isSelected: boolean;
  /** Children to render */
  children: ReactNode;
}

function ModeToggleButtonOption({ isSelected, children }: ModeToggleButtonOptionProps) {
  return (
    <div
      className={cn(
        "rounded-md px-md text-label-medium-strong",
        isSelected
          ? "bg-brand-primary py-xxs text-interactive-primary-button-enabled-foreground shadow-[inset_0_0.25rem_0.25rem_0_rgba(255,255,255,0.25)]"
          : "shadow-none",
      )}
    >
      {children}
    </div>
  );
}
